<!-- eslint-disable perfectionist/sort-imports -->
<!-- eslint-disable no-unused-vars -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { debounce } from 'lodash';
import range from 'lodash/range';
import {
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  FunnelX,
  Info,
  Search,
  SearchX,
  SquareArrowOutUpRight,
} from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import {
  computed, onMounted, reactive, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';

import FinancingsPagination from '~/components/financings/Pagination.vue';
import type { UseFinancingsParams } from '~/store/financing/useFinancingPaginate';
import financingsFilterSchema from '~/schemas/financingsFilterSchema';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import utils from '~/services/financing-flow-api/financing-flow.utils';
import useFinancingPaginate from '~/store/financing/useFinancingPaginate';
import useUsersFilter, { UseUsersParams } from '~/store/users/useUsersFilter';
import date from '~/utils/date';
import money from '~/utils/money';
import string, { formatName, normalizeString } from '~/utils/string';

import useSearchParamsState from '../hooks/useSearchParamsState';
import DashboardLayout from '../layouts/DashboardLayout.vue';

const router = useRouter();

// INITIAL PARAMS
const responsibleNameModel = ref('');
const debouncedResponsibleName = ref(responsibleNameModel.value);
const sortState = ref({
  column: '',
  direction: '',
});
const INITIAL_FILTER_STATE: Omit<UseFinancingsParams, 'page_size'> = {
  page: '1',
};

const INITIAL_PERSON_FILTER: UseUsersParams = {
  only_gc: true,
  page: 1,
  page_size: utils.DEFAULT_PAGE_SIZE,
  partial_name: '',
};
const columns = [
  { name: 'Nome do cliente', sort: true, value: 'customer_name' },
  { name: 'ID', sort: true, value: 'id' },
  { name: 'Combo Fácil', sort: true, value: 'is_combo' },
  { name: 'Valor Financiado', sort: true, value: 'financed_value' },
  { name: 'Expira em', sort: true, value: 'expires_at' },
  { name: 'Nome do responsável (GC)', sort: true, value: 'responsible_name' },
  { name: 'Integrador', sort: true, value: 'partner_name' },
  { name: 'Etapa', sort: false, value: 'step' },
  { name: 'Status', sort: false, value: 'status' },
  { name: 'Ação', sort: false, value: 'action' },
];
// SEARCH PARAMS
const searchParams = useSearchParamsState<Omit<UseFinancingsParams, 'page_size'>>(INITIAL_FILTER_STATE, {
  parseArrays: false,
});

const additionalParams = reactive({
  page_size: utils.DEFAULT_PAGE_SIZE,
});

// FILTER FORMS
const filterForm = useForm({
  validationSchema: toTypedSchema(financingsFilterSchema),
});

// MODELS AND PROPS
const [statusModel, statusProps] = filterForm.defineField('statuses');
const [stepsModel, stepsProps] = filterForm.defineField('steps');
const [batteriesModel, batteriesProps] = filterForm.defineField('is_battery');
const [personTypeModel, personTypeProps] = filterForm.defineField('person_type');
const [reassessmentTypeModel, reassessmentTypeProps] = filterForm.defineField('reassessment');
const [customerNameModel, customerNameProps] = filterForm.defineField('customer_name');
const [partnerDocumentModel, partnerDocumentProps] = filterForm.defineField('partner_document');
const [customerDocumentModel, customerDocumentProps] = filterForm.defineField('customer_document');
const [financingIDModel, financingIDProps] = filterForm.defineField('financing_id');
const [partnerIDModel, partnerIDProps] = filterForm.defineField('partner_id');
const [projectIDModel, projectIDProps] = filterForm.defineField('project_id');
const [comboModel, comboProps] = filterForm.defineField('is_combo');

const searchPersonParams = computed(() => ({
  ...INITIAL_PERSON_FILTER,
  partial_name: debouncedResponsibleName.value || '',
}));

// API CALLS

const financingParams = computed(() => ({
  ...searchParams.value,
  ...additionalParams,
}));

const {
  data: financings,
  isLoading: isLoadingFinancings,
} = useFinancingPaginate(financingParams);

const {
  data: responsible,
  isLoading: isLoadingResponsables,
} = useUsersFilter(searchPersonParams);

// UTILS
const hasNext = computed(() => !!financings.value?.has_next);
const pageSize = computed(() => additionalParams.page_size || utils.DEFAULT_PAGE_SIZE);
const baseUrl = import.meta.env.VITE_FINANCING_PLATFORM_API_URL;

const filteredResponsibles = computed(() => {
  if (responsible.value?.length && filterForm.values.responsible_id === undefined) {
    return responsible.value.filter(
      (item) => item.fullname.toLowerCase().includes((responsibleNameModel.value ?? '').toLowerCase()),
    );
  }
  return [];
});

const sortedFinancings = computed(() => {
  const data = financings.value?.data;
  if (!data) return [];

  const { column, direction } = sortState.value;
  const isAscending = direction === 'asc';

  const getColumnValue = (item: any) => {
    if (column === 'expires_at') return item[column] || Infinity;
    if (column === 'financed_value' || column === 'id') return item[column] ?? 0;
    return item[column]?.toString().toLowerCase() || '';
  };

  return [...data].sort((firstItem, secondItem) => {
    const firstValue = getColumnValue(firstItem);
    const secondValue = getColumnValue(secondItem);

    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return isAscending ? firstValue - secondValue : secondValue - firstValue;
    }

    return isAscending
      ? firstValue.localeCompare(secondValue)
      : secondValue.localeCompare(firstValue);
  });
});

const updateDebouncedName = debounce((val: string) => {
  debouncedResponsibleName.value = val;
}, 300);

// FUNCTIONS

function cleanedDocument(document: string) {
  return document.replace(/\D/g, '');
}

function expiredFinancing(financing: FinancingFlowApi.FinancingPaginate) {
  const isExpired = financing.is_expired || financing.status === 'expired';
  const isFormalized = financing.formalized_at;
  const isInstallated = financing.step === 'installation';
  const expiredByStatus = financing.status === 'reproved' || financing.status === 'canceled';
  const expirationDate = date(financing.expires_at);
  const today = date();
  const daysUntilExpiration = expirationDate.diff(today, 'days');

  if (
    isExpired
    || isFormalized
    || expiredByStatus
    || daysUntilExpiration > 15
    || daysUntilExpiration < 0
    || isInstallated
  ) {
    return '-';
  }
  return expirationDate.fromNow(true);
}

function getRedirectUrl(financing: FinancingFlowApi.FinancingPaginate) {
  const routes = {
    default: () => `${baseUrl}/${financing.person_type === 'PJ' ? 'company' : 'customer'}/${financing.project_id}`,
    installation: () => `${baseUrl}/installation/${financing.project_id}`,
    simulation: () => `${baseUrl}/simulation/result/${financing.project_id}`,
  };

  return (routes[financing.step as keyof typeof routes] || routes.default)();
}

function handleReset() {
  searchParams.value = INITIAL_FILTER_STATE;
  responsibleNameModel.value = '';
  filterForm.resetForm();
}

function handleResetResponsibleFilter() {
  filterForm.setFieldValue('responsible_id', undefined);
}

function handleSearch() {
  return filterForm.handleSubmit((values) => {
    searchParams.value = {
      ...searchParams.value,
      ...values,
      customer_document: cleanedDocument(values.customer_document || ''),
      is_battery: batteriesModel.value,
      is_combo: comboModel.value,
      page: '1',
      partner_document: cleanedDocument(values.partner_document || ''),
      person_type: personTypeModel.value || '',
      responsible_name: responsibleNameModel.value,
      statuses: statusModel.value || '',
      steps: stepsModel.value || '',
    };
  })();
}

function navigateToDetails(financing: FinancingFlowApi.FinancingPaginate) {
  router.push(`/financing/${financing.project_id}`);
}

function redirect(financing: FinancingFlowApi.FinancingPaginate) {
  const url = getRedirectUrl(financing);
  window.open(url, '_blank');
}

function selectResponsible(item: { fullname: string; id: number }) {
  responsibleNameModel.value = item.fullname;
  filterForm.setFieldValue('responsible_id', item.id);
}

function setPage(value: number) {
  if (hasNext.value) {
    searchParams.value = {
      ...searchParams.value,
      page: value.toString(),
    };
  }
}

function toggleSort(column: string, defaultDirection: string) {
  const isSameColumn = sortState.value.column === column;
  const isAscending = sortState.value.direction === 'asc';
  const applyDirection = isAscending ? 'desc' : 'asc';

  sortState.value.column = column;
  sortState.value.direction = isSameColumn ? applyDirection : defaultDirection;
}

function updateStatusOptions(step: string) {
  const basicStatus = ['approved', 'reproved', 'expired', 'canceled'];

  const stepFilters: Record<string, () => typeof utils.STATUS_OPTIONS> = {
    biometry_and_contract: () => utils.STATUS_OPTIONS.filter(
      (option) => option.value !== 'approved' && option.value !== 'to_review',
    ),

    customer_data: () => utils.STATUS_OPTIONS.filter(
      (option) => option.value !== 'waiting_customer' && option.value !== 'approved',
    ),

    equipment: () => {
      const equipmentStatus = basicStatus.map((status) => (status === 'approved' ? 'in_progress' : status));
      return utils.STATUS_OPTIONS.filter((option) => equipmentStatus.includes(option.value));
    },

    installation: () => utils.STATUS_OPTIONS.filter(
      (option) => option.value !== 'waiting_customer' && option.value !== 'expired',
    ),

    payment_model: () => utils.STATUS_OPTIONS.filter(
      (option) => option.value !== 'approved' && option.value !== 'reproved',
    ),

    simulation: () => utils.STATUS_OPTIONS.filter((option) => basicStatus.includes(option.value)),
  };

  const filterFn = stepFilters[step];
  return filterFn ? filterFn() : utils.STATUS_OPTIONS;
}

function updateStepOptions(status: string) {
  const stepsToReview = ['customer_data', 'payment_model', 'installation'];

  const statusFilters: Record<string, () => typeof utils.STEP_OPTIONS> = {
    approved: () => utils.STEP_OPTIONS.filter(
      (option) => option.value === 'simulation' || option.value === 'installation',
    ),
    expired: () => utils.STEP_OPTIONS.filter((option) => option.value !== 'installation'),
    in_progress: () => utils.STEP_OPTIONS.filter((option) => option.value !== 'simulation'),
    reproved: () => utils.STEP_OPTIONS.filter((option) => option.value !== 'payment_model'),
    to_review: () => utils.STEP_OPTIONS.filter((option) => stepsToReview.includes(option.value)),
    under_analysis: () => utils.STEP_OPTIONS.filter(
      (option) => stepsToReview.concat(['biometry_and_contract']).includes(option.value),
    ),
    waiting_customer: () => utils.STEP_OPTIONS.filter(
      (option) => option.value === 'biometry_and_contract' || option.value === 'payment_model',
    ),
  };

  const filterFn = statusFilters[status];
  return filterFn ? filterFn() : utils.STEP_OPTIONS;
}

const computedStatusOptions = computed(() => {
  if (stepsModel.value !== undefined) {
    return updateStatusOptions(stepsModel.value);
  }
  return utils.STATUS_OPTIONS;
});

const computedBatteriesOptions = computed(() => utils.BATTERY_OPTIONS);

const computedPersonTypeOptions = computed(() => utils.PERSON_TYPE_OPTIONS);

const computedReassessmentOptions = computed(() => utils.REASSESSMENT_OPTIONS);

const computedStepOptions = computed(() => {
  if (statusModel.value !== undefined) {
    return updateStepOptions(statusModel.value);
  }
  return utils.STEP_OPTIONS;
});

// WATCHERS

watch(responsibleNameModel, (newVal) => {
  if ((newVal?.length || 0) <= 1) {
    filterForm.setFieldValue('responsible_id', undefined);
  }
  updateDebouncedName(newVal);
});

watch(() => filteredResponsibles.value, (newVal) => {
  if (newVal.length === 1) {
    const isEqual = normalizeString(newVal?.[0]?.fullname) === normalizeString(responsibleNameModel.value);
    if (isEqual) {
      filterForm.setFieldValue('responsible_id', newVal[0].id);
    } else {
      filterForm.setFieldValue('responsible_id', undefined);
    }
  }
});

onMounted(() => {
  document.title = 'Financiamentos ';

  filterForm.setValues({
    customer_document: searchParams.value.customer_document || '',
    customer_name: searchParams.value.customer_name || '',
    financing_id: searchParams.value.financing_id ? Number(searchParams.value.financing_id) || undefined : undefined,
    is_battery: searchParams.value.is_battery || undefined,
    is_combo: searchParams.value.is_combo || undefined,
    partner_document: searchParams.value.partner_document || '',
    partner_id: searchParams.value.partner_id ? Number(searchParams.value.partner_id) || undefined : undefined,
    person_type: searchParams.value.person_type || undefined,
    project_id: searchParams.value.project_id || '',
    reassessment: searchParams.value.reassessment || undefined,
    responsible_id: searchParams.value.responsible_id || undefined,
    statuses: searchParams.value.statuses || undefined,
    steps: searchParams.value.steps || undefined,
  });

  if (searchParams.value.responsible_id) {
    responsibleNameModel.value = searchParams.value?.responsible_name || '';
    handleResetResponsibleFilter();
  }
});
</script>

<template>
  <DashboardLayout title="Financiamentos" description="Lista de financiamentos">
    <div class="row row-cards">
      <div class="col-12">
        <div class="card position-relative">
          <div class="card-body border-bottom py-3">
            <div class="row w-full">
              <div class="col-md-6 col-xl-3">
                <div class="mb-3">
                  <label class="form-label" for="partial_name">Responsável (GC)</label>
                  <div>
                    <div class="input-icon mb-3">
                      <input
                        type="text"
                        autocomplete="off"
                        id="partial_name"
                        class="form-control"
                        placeholder="Busque pelo nome do GC"
                        v-model="responsibleNameModel"
                        @click="handleResetResponsibleFilter"
                      />
                      <span class="input-icon-addon">
                        <Search class="icon icon-1" />
                      </span>
                    </div>
                    <!-- Lista de sugestões -->
                    <ul
                      v-if="(responsibleNameModel ?? '').length > 1
                        && filteredResponsibles.length > 0
                        && !isLoadingResponsables"
                      class="
                        bg-white border rounded position-absolute
                        z-1 max-h-48 size-dropdown overflow-auto list-group
                      ">
                      <li
                        v-for="item in filteredResponsibles"
                        :key="item.id"
                        class="p-2 cursor-pointer"
                        @click="selectResponsible(item)"
                        @keydown.enter="selectResponsible(item)"
                        tabindex="0">
                        {{ item.fullname }}
                      </li>
                    </ul>
                    <div
                      v-if="(responsibleNameModel ?? '').length > 1
                        && (responsible ?? []).length === 0
                        && !isLoadingResponsables"
                      class="
                        p-2 text-center size-dropdown bg-white border rounded
                        position-absolute z-1 overflow-auto list-group
                      ">
                      <div class="d-flex justify-content-center p-1">
                        <span class="text-secondary">
                          <SearchX class="icon icon-1" />
                          Nenhum resultado encontrado
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-3">
                <div class="mb-3">
                  <label class="form-label" for="customer_name">Nome do cliente</label>
                  <div class="input-icon mb-3">
                    <input
                      type="text"
                      id="customer_name"
                      class="form-control"
                      placeholder="Busque pelo nome do cliente"
                      v-model="customerNameModel"
                      v-bind="customerNameProps"
                      @keydown.enter="handleSearch"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="project_id">ID do projeto</label>
                  <div class="input-icon mb-3">
                    <input
                      type="text"
                      id="project_id"
                      class="form-control"
                      placeholder="Busque pelo ID do projeto"
                      v-model="projectIDModel"
                      v-bind="projectIDProps"
                      @keydown.enter="handleSearch" />
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="partner_id">Parceiro ID</label>
                  <div class="input-icon mb-3">
                    <input
                      type="number"
                      id="partner_id"
                      class="form-control"
                      placeholder="Busque pelo ID"
                      v-model="partnerIDModel"
                      v-bind="partnerIDProps"
                      @keydown.enter="handleSearch"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="financing_id">ID do financiamento</label>
                  <div class="input-icon mb-3">
                    <input
                      type="number"
                      id="financing_id"
                      class="form-control"
                      placeholder="Busque pelo ID"
                      v-model="financingIDModel"
                      v-bind="financingIDProps"
                      @keydown.enter="handleSearch" />
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="partner_document">CNPJ do integrador</label>
                  <div class="input-icon mb-3">
                    <input
                      type="text"
                      id="partner_document"
                      class="form-control"
                      placeholder="Busque pelo CNPJ"
                      v-model="partnerDocumentModel"
                      v-bind="partnerDocumentProps"
                      @keydown.enter="handleSearch"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="customer_document">CPF/CNPJ do cliente</label>
                  <div class="input-icon mb-3">
                    <input
                      type="text"
                      id="customer_document"
                      class="form-control"
                      placeholder="Busque pelo CPF/CNPJ"
                      v-model="customerDocumentModel"
                      v-bind="customerDocumentProps"
                      @keydown.enter="handleSearch"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label disabled" for="step">Etapa</label>
                  <select
                    id="step"
                    class="form-select disabled"
                    v-model="stepsModel"
                    v-bind="stepsProps"
                    aria-label="Campo de seleção de etapa"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option
                      v-for="option in computedStepOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="statuses">Status</label>
                  <select
                    id="statuses"
                    class="form-select"
                    v-model="statusModel"
                    v-bind="statusProps"
                    aria-label="Campo de seleção de status"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option
                      v-for="option in computedStatusOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="person_type">Tipo de financiamento</label>
                  <select
                    id="person_type"
                    class="form-select"
                    v-model="personTypeModel"
                    v-bind="personTypeProps"
                    aria-label="Campo de seleção de tipo de financiamento"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option
                      v-for="option in computedPersonTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label disabled" for="is_combo">Combo</label>
                  <select
                    id="is_combo"
                    class="form-select disabled"
                    v-model="comboModel"
                    v-bind="comboProps"
                    aria-label="Campo de seleção de combo facil"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option :value="true">Sim</option>
                    <option :value="false">Não</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="is_battery">Bateria</label>
                  <select
                    id="is_battery"
                    class="form-select"
                    v-model="batteriesModel"
                    v-bind="batteriesProps"
                    aria-label="Campo de seleção se está ou não com bateria"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option
                      v-for="option in computedBatteriesOptions"
                      :key="option.name"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6 col-xl-2">
                <div class="mb-3">
                  <label class="form-label" for="reassessment">Repescagem</label>
                  <select
                    id="reassessment"
                    class="form-select"
                    v-model="reassessmentTypeModel"
                    v-bind="reassessmentTypeProps"
                    aria-label="Campo de seleção de repescagem"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option
                      v-for="option in computedReassessmentOptions"
                      :key="option.name"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6 col-xl-3">
                <label class="form-label" for="query">{{
                  string.INVISIBEL_CHARACTER
                }}</label>
                <div class="row">
                  <div class="col">
                    <button
                      type="button"
                      class="btn btn-secondary w-full"
                      @click="handleSearch"
                    >
                      Buscar
                      <Search class="icon icon-right" />
                    </button>
                  </div>
                  <div class="col">
                    <button
                      type="button"
                      class="btn btn-white w-full"
                      @click="handleReset">
                      Limpar Filtros
                      <FunnelX class="icon icon-right" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-vcenter">
              <thead>
                <tr>
                  <th v-for="column in columns" :key="column.value" scope="col">
                    <div class="d-flex align-items-center justify-content-between break-anywhere">
                      <span>{{ column.name }}</span>

                      <div v-if="column.sort" class="d-flex flex-column cursor-pointer border rounded-1 mx-2">
                        <ChevronUp
                          class="icon w-3 h-3"
                          @click="toggleSort(column.value, 'asc')"
                          :stroke="sortState.column === column.value
                            && sortState.direction === 'asc'
                            ? 'black'
                            : 'gray'"
                        />
                        <ChevronDown
                          class="icon w-3 h-3"
                          @click="toggleSort(column.value, 'desc')"
                          :stroke="sortState.column === column.value
                            && sortState.direction === 'desc'
                            ? 'black'
                            : 'gray'"
                        />

                      </div>
                    </div>
                  </th>
                  <th />
                </tr>
              </thead>
              <template v-if="isLoadingFinancings">
                <tbody>
                  <tr v-for="(_, index) in range(pageSize + 1)" :key="index">
                    <td v-for="i in range(8)" :key="i">
                      <div class="placeholder-glow" style="height: 19.5px">
                        <span class="placeholder col-12" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
              <tbody v-else>
                <template v-if="sortedFinancings.length">
                  <tr
                    v-for="(financing, index) in sortedFinancings"
                    :key="index"
                    class="cursor-pointer"
                    @dblclick="navigateToDetails(financing)"
                  >
                    <td>
                      <span class="text-secondary">{{ formatName(financing.customer_name) }}
                      </span>
                    </td>
                    <td>
                      <b class="text-reset" tabindex="-1">{{ financing.id }}</b>
                    </td>
                    <td>
                      <div class="d-flex justify-content-center">
                        <span
                          v-if="financing.is_combo"
                          class="badge bg-lime-lt">Sim</span>
                        <span v-else class="badge bg-red-lt"> Não </span>
                      </div>
                    </td>
                    <td>
                      <b class="text-secondary">{{
                        money.formatBRL(financing.financed_value)
                      }}</b>
                    </td>
                    <td>
                      <div class="d-flex justify-content-center">
                        {{ expiredFinancing(financing) }}
                      </div>
                    </td>
                    <td>{{ formatName(financing.responsible_name) }}</td>
                    <td>{{ formatName(financing.partner_name) }}</td>
                    <td class="text-nowrap">
                      <span class="tag">
                        <span
                          class="badge badge-3 text-indigo-fg tag-status badge-dot"
                          :class="`bg-${utils.getStepColorClass(financing.step as FinancingFlowApi.FinancingSteps)}`" />
                        {{ utils.getStepLabel(financing.step as FinancingFlowApi.FinancingSteps) }}
                      </span>
                    </td>
                    <td class="text-nowrap">
                      <span
                        class="badge"
                        :class="`bg-${utils.getStatusColorClass(
                          financing.status as FinancingFlowApi.FinancingStatus,
                        )}-lt`">{{ utils.getStatusLabel(financing.status as FinancingFlowApi.FinancingStatus) }}</span>
                    </td>
                    <td>
                      <div class="dropdown">
                        <a href="#" data-bs-toggle="dropdown" aria-label="More options">
                          <EllipsisVertical class="icon icon-1" stroke="grey" />
                        </a>
                        <div class="dropdown-menu">
                          <a class="dropdown-item" :href="`financing/${financing.project_id}`">
                            <Info class="icon icon-sm me-1" />
                            Detalhes
                          </a>
                          <a class="dropdown-item" href="#" @click="redirect(financing)">
                            <SquareArrowOutUpRight class="icon icon-sm me-1" />
                            Abrir na plataforma
                          </a>
                        </div>
                      </div>
                    </td>
                    <td />
                  </tr>
                </template>
                <template v-else>
                  <td colspan="12">
                    <div class="d-flex justify-content-center">
                      <span>Nenhuma informação encontrada</span>
                    </div>
                  </td>
                </template>
              </tbody>
            </table>
          </div>
          <!-- Pagination -->
          <div class="d-flex justify-content-end gap-2 my-3 pe-5 w-full">
            <div class="d-flex">
              <label class="form-label w-100" for="status">Itens por página</label>
              <select
                id="status"
                class="form-select"
                v-model="additionalParams.page_size"
                @change="handleSearch">
                <option v-for="[value, label] in Object.entries(utils.pageSizeMap)" :value="value" :key="value">
                  {{ label }}
                </option>
              </select>
            </div>
            <FinancingsPagination
              :page="Number(searchParams.page)"
              :has-next-pagination="hasNext"
              @change-page="setPage"
            />
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
<style scoped lang="scss">
.table-responsive {
  min-height: 50vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table {
  tbody {
    tr {
      background-color: white !important;

      &:hover {
        background-color: #f8f9fa !important;
      }

      &.cursor-pointer {
        cursor: pointer;
      }
    }
  }
}

.size-dropdown {
  max-width: 345px;
}

li {
  &:hover {
    opacity: 0.5;
  }
}
</style>
